(() => {
  const PREVIEW_TRIGGER_SELECTOR = '[data-cart-preview-zoom]';
  const LIGHTBOX_ID = 'CartCustomPreviewLightbox';
  const PRODUCT_VIEW = 'product';
  const UPLOADED_VIEW = 'uploaded';
  let previouslyFocusedElement = null;
  let previousBodyOverflow = '';
  let activeGalleryItems = [];
  let activeGalleryIndex = 0;
  let trackedVisualViewport = null;
  let trackedLightbox = null;
  let viewportUpdateFrame = 0;
  let consumePreviewEscape = false;
  const designOriginalRequests = new Map();

  /**
   * Accepts only normal HTTP(S) image links before assigning them to the lightbox.
   * This protects the modal from malformed line-item property values.
   */
  function getSafeHttpUrl(value) {
    if (!value) return '';

    try {
      const parsedUrl = new URL(value, window.location.origin);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:' ? parsedUrl.href : '';
    } catch (error) {
      return '';
    }
  }

  /**
   * A creative design has several originals but only one flattened preview.
   * Match its immutable job URL to its manifest rather than inferring an area
   * from a label/order; named, mixed and reordered upload slots stay isolated.
   * Source properties may be sparse after duplicate URLs are removed at cart
   * submission. Original aliases and remaining source properties form a union;
   * the filename's photo number, not the property position, orders the photos.
   */
  function getDesignSources(trigger, productUrl) {
    const preview = new URL(productUrl);
    const isCreativePreview = /\/gio-[a-z0-9-]+-preview\.png$/i.test(preview.pathname);
    const holder = trigger.closest('[data-cart-design-properties]');
    if (!holder) return { isCreative: isCreativePreview, urls: [] };
    let properties;
    try { properties = JSON.parse(holder.dataset.cartDesignProperties || '{}'); }
    catch (error) { return { isCreative: isCreativePreview, urls: [] }; }
    if (!properties || Array.isArray(properties) || typeof properties !== 'object') {
      return { isCreative: isCreativePreview, urls: [] };
    }
    for (const name of Object.keys(properties)) {
      const match = /^_DesignManifest(\d+)$/.exec(name);
      if (!match) continue;
      const manifest = designAssetUrl(properties[name]);
      if (!manifest) continue;
      if (!/-design\.json$/.test(manifest.pathname) || manifest.origin !== preview.origin ||
          manifest.pathname.replace(/-design\.json$/, '-preview.png') !== preview.pathname) continue;
      const sourcePrefix = '_DesignSource' + match[1] + '_';
      const jobPath = manifest.pathname.replace(/-design\.json$/, '-photo-');
      const sourceNames = Object.keys(properties).filter(key => key.startsWith(sourcePrefix));
      const sources = new Map();
      // Universal/file templates support up to twelve source files. Free-form
      // designs and older templates have lower limits, so this also covers them.
      const maxSources = 12;
      function sourcePhoto(value) {
        const original = designAssetUrl(value);
        if (!original || original.origin !== manifest.origin || !original.pathname.startsWith(jobPath)) return null;
        const photo = /^(0|[1-9]\d*)\.(jpg|jpeg|png|webp|heic|heif|avif|tif|tiff|gif|bmp|bin)$/i.exec(original.pathname.slice(jobPath.length));
        if (!photo || Number(photo[1]) >= maxSources) return null;
        return { index: Number(photo[1]), url: original.href };
      }
      // Explicit sources belong to this exact manifest. A malformed entry must
      // not turn into a plausible but incomplete collage via the alias fallback.
      if (sourceNames.length > maxSources) return { isCreative: true, urls: [] };
      for (const sourceName of sourceNames) {
        const position = sourceName.slice(sourcePrefix.length);
        if (!/^(0|[1-9]\d*)$/.test(position) || Number(position) >= maxSources) return { isCreative: true, urls: [] };
        const source = sourcePhoto(properties[sourceName]);
        if (!source) return { isCreative: true, urls: [] };
        sources.set(source.url, source);
      }
      for (const alias of Object.keys(properties)) {
        if (!/^_?Original(?:Photo)?(?:\d+| - .+)?$/.test(alias)) continue;
        const source = sourcePhoto(properties[alias]);
        // Aliases may refer to another print area or a legacy upload on the
        // same cart line; only the current immutable job can supply its photos.
        if (source) sources.set(source.url, source);
      }
      const photos = Array.from(sources.values()).sort((left, right) => left.index - right.index || left.url.localeCompare(right.url));
      if (photos.length > maxSources) return { isCreative: true, urls: [] };
      const layeredJob = /^\/orders\/(gio-layered-[a-z0-9]{1,24}-[a-f0-9]{32})-design\.json$/.exec(manifest.pathname);
      const trustedManifest = manifest.protocol === 'https:' && !manifest.port && !manifest.search && !manifest.hash &&
        (manifest.hostname === 'static.giftinone.com' || /^giftinone\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/.test(manifest.hostname));
      const productId = String(holder.dataset.cartDesignProductId || ''), variantId = String(holder.dataset.cartDesignVariantId || '');
      // Compact layered orders retain their exact saved manifest. Legacy source
      // properties keep the original synchronous path, including fail-closed
      // handling of malformed explicit entries above.
      const sourceManifest = !sourceNames.length && layeredJob && trustedManifest && /^\d{1,20}$/.test(productId) && /^\d{1,20}$/.test(variantId)
        ? { url: manifest.href, jobId: layeredJob[1], productId, variantId, previewKey: preview.pathname.slice(1) } : null;
      return { isCreative: true, urls: photos.map(photo => photo.url), sourceManifest };
    }
    return { isCreative: isCreativePreview, urls: [] };
  }

  /** Design contracts contain absolute object URLs, never relative links or credentials. */
  function designAssetUrl(value) {
    if (typeof value !== 'string' || !/^https?:\/\//i.test(value) || /[\u0000-\u0020\\]/.test(value)) return null;
    try {
      const url = new URL(value);
      return !url.username && !url.password && (url.protocol === 'https:' || url.protocol === 'http:') ? url : null;
    } catch (error) { return null; }
  }

  /** Read only this cart item's immutable design. This never authorizes HD. */
  async function loadDesignOriginals(reference) {
    if (!reference) return [];
    const cacheKey = [reference.url, reference.productId, reference.variantId].join('\n');
    if (designOriginalRequests.has(cacheKey)) return designOriginalRequests.get(cacheKey);
    const request = (async () => {
      const controller = new AbortController(), maximum = 8 * 1024 * 1024;
      const timeout = window.setTimeout(() => controller.abort(), 10000);
      try {
        const response = await window.fetch(reference.url, { credentials: 'omit', redirect: 'error', cache: 'force-cache', signal: controller.signal });
        if (!response.ok || Number(response.headers.get('Content-Length')) > maximum) throw new Error('Saved design unavailable');
        let bytes;
        if (response.body && typeof response.body.getReader === 'function') {
          const reader = response.body.getReader(), chunks = []; let size = 0;
          try {
            while (true) {
              const part = await reader.read();
              if (part.done) break;
              size += part.value.byteLength;
              if (size > maximum) throw new Error('Saved design exceeds its limit');
              chunks.push(part.value);
            }
          } catch (error) { await reader.cancel().catch(() => {}); throw error; }
          finally { reader.releaseLock(); }
          bytes = new Uint8Array(size); let offset = 0;
          chunks.forEach(chunk => { bytes.set(chunk, offset); offset += chunk.byteLength; });
        } else {
          bytes = new Uint8Array(await response.arrayBuffer());
        }
        if (!bytes.byteLength || bytes.byteLength > maximum) throw new Error('Saved design exceeds its limit');
        const manifest = JSON.parse(new TextDecoder().decode(bytes));
        if (manifest?.schema !== 'gio-layered-design-v1' || manifest.jobId !== reference.jobId ||
            String(manifest.productId) !== reference.productId || String(manifest.variantId) !== reference.variantId ||
            manifest.previewKey !== reference.previewKey) throw new Error('Saved design does not match this cart item');
        const keys = Array.isArray(manifest.sourceKeys) ? manifest.sourceKeys
          : Array.isArray(manifest.photos) ? [...new Set(manifest.photos.map(photo => photo && photo.key))] : null;
        if (!keys || keys.length > 12) throw new Error('Saved design source list is invalid');
        const prefix = 'orders/' + reference.jobId + '-photo-', originals = [], seen = new Set();
        keys.forEach(key => {
          if (typeof key !== 'string' || !key.startsWith(prefix)) throw new Error('Source belongs to another design');
          const part = /^(0|[1-9]|1[01])\.(jpg|jpeg|png|webp|heic|heif|avif|tif|tiff|gif|bmp|bin)$/i.exec(key.slice(prefix.length));
          if (!part || seen.has(Number(part[1]))) throw new Error('Saved design source list is invalid');
          seen.add(Number(part[1]));
          originals.push({ index: Number(part[1]), url: new URL('/' + key, reference.url).href });
        });
        return originals.sort((a, b) => a.index - b.index).map(photo => photo.url);
      } finally { window.clearTimeout(timeout); }
    })();
    if (designOriginalRequests.size >= 40) designOriginalRequests.delete(designOriginalRequests.keys().next().value);
    designOriginalRequests.set(cacheKey, request);
    request.catch(() => { if (designOriginalRequests.get(cacheKey) === request) designOriginalRequests.delete(cacheKey); });
    return request;
  }

  function hydrateDesignOriginals(lightbox, item) {
    if (!item.sourceManifest || item.originalsLoading || item.originalsLoaded) return;
    item.originalsLoading = true;
    loadDesignOriginals(item.sourceManifest).then(urls => {
      item.originalsLoaded = true;
      item.uploadedUrls = urls; item.uploadedUrl = urls[0] || ''; item.uploadedIndex = 0;
      if (!lightbox.hidden && activeGalleryItems[activeGalleryIndex] === item) {
        showGalleryItem(lightbox, activeGalleryIndex, lightbox.dataset.activeView || PRODUCT_VIEW);
      }
    }).catch(() => {
      // The finished preview remains usable if originals cannot be retrieved.
      // A later open retries; an old request never changes the current item.
    }).finally(() => { item.originalsLoading = false; });
  }

  /** Turn the existing production face map into the shared cart photo gallery. */
  function hydrateCubeGallery(wrapper) {
    if (wrapper.dataset.cubeGalleryReady === 'true') return;
    wrapper.dataset.cubeGalleryReady = 'true';
    let faces;
    try {
      const faceMap = JSON.parse(wrapper.dataset.pics || '');
      if (!faceMap || typeof faceMap !== 'object' || Array.isArray(faceMap)) return;
      const keys = Object.keys(faceMap).sort((left, right) => Number(left) - Number(right));
      if (!keys.length || keys.length > 9) return;
      faces = keys.map((key) => {
        if (!/^[0-8]$/.test(key) || !faceMap[key] || typeof faceMap[key].src !== 'string') throw new Error('Invalid face');
        const rawUrl = faceMap[key].src;
        if (!/^https?:\/\//i.test(rawUrl)) throw new Error('Invalid photo URL');
        const url = getSafeHttpUrl(rawUrl);
        if (!url) throw new Error('Invalid photo URL');
        return { url, number: Number(key) + 1 };
      });
    } catch (error) {
      return;
    }

    function element(tag, className, text) {
      const node = document.createElement(tag);
      node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }
    const title = wrapper.dataset.productTitle || 'Your photo cube';
    const summary = element('div', 'cart-personalization-gallery__summary');
    const check = element('span', 'cart-personalization-gallery__check', '✓');
    check.setAttribute('aria-hidden', 'true');
    const summaryText = element('div', 'cart-personalization-gallery__summary-text');
    summaryText.append(
      element('p', 'cart-personalization-gallery__title', `${faces.length} photos saved`),
      element('p', 'cart-personalization-gallery__hint', 'Select a photo to enlarge.')
    );
    summary.append(check, summaryText);
    const list = element('ul', 'cart-personalization-gallery__thumbnails');
    list.setAttribute('role', 'list');
    list.setAttribute('aria-label', 'Your saved cube faces');
    faces.forEach((face) => {
      const label = `Face ${face.number}`;
      const entry = element('li', 'cart-personalization-gallery__entry');
      const link = element('a', 'cart-personalization-gallery__item');
      link.href = face.url;
      link.setAttribute('data-cart-preview-zoom', '');
      link.setAttribute('data-cart-preview-gallery-item', '');
      link.dataset.previewUrl = face.url;
      link.dataset.productTitle = title;
      link.dataset.photoLabel = label;
      link.dataset.previewAlt = `${label} of ${title}`;
      link.setAttribute('aria-label', `Review ${label} of ${title}`);
      const thumbnail = element('span', 'cart-personalization-gallery__thumbnail');
      const photo = element('img', 'cart-personalization-gallery__image');
      photo.src = face.url;
      photo.alt = `${label} of ${title}`;
      photo.width = 64;
      photo.height = 64;
      photo.loading = 'lazy';
      const number = element('span', 'cart-personalization-gallery__number', String(face.number));
      number.setAttribute('aria-hidden', 'true');
      thumbnail.append(photo, number);
      link.append(thumbnail, element('span', 'cart-personalization-gallery__label', label));
      entry.appendChild(link);
      list.appendChild(entry);
    });
    wrapper.replaceChildren(summary, list);
  }

  /** Cart drawer and notification markup is replaced after each Ajax cart write. */
  function hydrateCubeGalleries(root) {
    if (root.nodeType === 1 && root.matches('[data-cube-cart-gallery]')) hydrateCubeGallery(root);
    if (root.querySelectorAll) root.querySelectorAll('[data-cube-cart-gallery]').forEach(hydrateCubeGallery);
  }

  /** Transparent AI preview overlays must remain Shopify Files. */
  function getSafePreviewOverlayUrl(value) {
    const safeUrl = getSafeHttpUrl(value);
    if (!safeUrl) return '';
    try {
      const parsedUrl = new URL(safeUrl);
      const cdnFile = parsedUrl.hostname === 'cdn.shopify.com' &&
        (parsedUrl.pathname.startsWith('/s/files/') || parsedUrl.pathname.startsWith('/cdn/shop/files/'));
      const storefrontFile = parsedUrl.pathname.startsWith('/cdn/shop/files/') &&
        ['giftinone.com', 'www.giftinone.com', 'gift-in-one.myshopify.com', window.location.hostname]
          .includes(parsedUrl.hostname);
      return cdnFile || storefrontFile ? safeUrl : '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Creates one reusable personalized-preview lightbox.
   * Event delegation keeps it available after Shopify replaces cart HTML through AJAX.
   */
  function getOrCreateLightbox() {
    let lightbox = document.getElementById(LIGHTBOX_ID);
    if (lightbox) return lightbox;

    lightbox = document.createElement('div');
    lightbox.id = LIGHTBOX_ID;
    lightbox.className = 'cart-custom-preview-lightbox';
    lightbox.hidden = true;
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-labelledby', `${LIGHTBOX_ID}Title`);
    lightbox.innerHTML = `
      <div class="cart-custom-preview-lightbox__panel" role="document">
        <button
          type="button"
          class="cart-custom-preview-lightbox__close"
          data-cart-preview-close
          aria-label="Close preview"
        >&times;</button>

        <header class="cart-custom-preview-lightbox__header">
          <p class="cart-custom-preview-lightbox__eyebrow" data-cart-preview-eyebrow>Your Preview</p>
          <h2 class="cart-custom-preview-lightbox__title" id="${LIGHTBOX_ID}Title">
            Your personalized design
          </h2>
        </header>

        <div
          class="cart-custom-preview-lightbox__switcher"
          data-cart-preview-switcher
          role="group"
          aria-label="Choose image to view"
          hidden
        >
          <button
            type="button"
            class="cart-custom-preview-lightbox__view-button is-active"
            data-cart-preview-view="${PRODUCT_VIEW}"
            aria-pressed="true"
          >Product preview</button>
          <button
            type="button"
            class="cart-custom-preview-lightbox__view-button"
            data-cart-preview-view="${UPLOADED_VIEW}"
            aria-pressed="false"
          >Uploaded photo</button>
        </div>

        <div class="cart-custom-preview-lightbox__media">
          <button
            type="button"
            class="cart-custom-preview-lightbox__gallery-button cart-custom-preview-lightbox__gallery-button--previous"
            data-cart-preview-gallery-previous
            aria-label="Previous photo"
            hidden
          >&#8249;</button>
          <span class="cart-custom-preview-lightbox__image-stack">
            <img class="cart-custom-preview-lightbox__image" src="" alt="">
            <img class="cart-custom-preview-lightbox__overlay" src="" alt="" aria-hidden="true" hidden>
          </span>
          <button
            type="button"
            class="cart-custom-preview-lightbox__gallery-button cart-custom-preview-lightbox__gallery-button--next"
            data-cart-preview-gallery-next
            aria-label="Next photo"
            hidden
          >&#8250;</button>
          <p
            class="cart-custom-preview-lightbox__gallery-counter"
            data-cart-preview-gallery-counter
            aria-live="polite"
            hidden
          ></p>
        </div>

        <footer class="cart-custom-preview-lightbox__footer">
          <div class="cart-custom-preview-lightbox__sources" data-cart-preview-sources
            role="group" aria-label="Choose an original photo" hidden></div>
          <div class="cart-custom-preview-lightbox__saved" aria-live="polite">
            <span class="cart-custom-preview-lightbox__check" aria-hidden="true">&#10003;</span>
            <div>
              <p class="cart-custom-preview-lightbox__saved-title" data-cart-preview-status-title>
                Personalized design saved
              </p>
              <p class="cart-custom-preview-lightbox__description" data-cart-preview-status-description>
                This is your personalized design preview.
              </p>
            </div>
          </div>
          <a
            class="cart-custom-preview-lightbox__full-size-link"
            data-cart-preview-full-size
            target="_blank"
            rel="noopener"
            hidden
          >Open uploaded photo full size (new tab) &#8599;</a>
        </footer>
      </div>
    `;

    document.body.appendChild(lightbox);
    return lightbox;
  }

  /**
   * Fits the overlay to the part of the page that is actually visible.
   * On iOS, the address and tab bars can resize the visual viewport without changing vh.
   */
  function applyVisualViewport(lightbox) {
    if (!lightbox || lightbox.hidden) return;

    const viewport = window.visualViewport;
    const viewportWidth = viewport ? viewport.width : window.innerWidth;
    const viewportHeight = viewport ? viewport.height : window.innerHeight;
    const viewportLeft = viewport ? viewport.offsetLeft : 0;
    const viewportTop = viewport ? viewport.offsetTop : 0;

    lightbox.style.setProperty('--cart-preview-viewport-width', `${viewportWidth}px`);
    lightbox.style.setProperty('--cart-preview-viewport-height', `${viewportHeight}px`);
    lightbox.style.setProperty('--cart-preview-viewport-left', `${viewportLeft}px`);
    lightbox.style.setProperty('--cart-preview-viewport-top', `${viewportTop}px`);
  }

  /** Coalesces rapid browser-toolbar viewport changes into one layout update per frame. */
  function scheduleVisualViewportUpdate() {
    if (viewportUpdateFrame) return;

    viewportUpdateFrame = window.requestAnimationFrame(() => {
      viewportUpdateFrame = 0;
      applyVisualViewport(trackedLightbox);
    });
  }

  /** Starts viewport tracking only while the preview is open. */
  function startVisualViewportTracking(lightbox) {
    stopVisualViewportTracking();
    trackedLightbox = lightbox;
    trackedVisualViewport = window.visualViewport || null;
    applyVisualViewport(lightbox);

    window.addEventListener('resize', scheduleVisualViewportUpdate, { passive: true });
    window.addEventListener('orientationchange', scheduleVisualViewportUpdate, { passive: true });
    if (trackedVisualViewport) {
      trackedVisualViewport.addEventListener('resize', scheduleVisualViewportUpdate, { passive: true });
      trackedVisualViewport.addEventListener('scroll', scheduleVisualViewportUpdate, { passive: true });
    }
  }

  /** Removes temporary viewport metrics and listeners after the preview closes. */
  function stopVisualViewportTracking() {
    window.removeEventListener('resize', scheduleVisualViewportUpdate);
    window.removeEventListener('orientationchange', scheduleVisualViewportUpdate);
    if (trackedVisualViewport) {
      trackedVisualViewport.removeEventListener('resize', scheduleVisualViewportUpdate);
      trackedVisualViewport.removeEventListener('scroll', scheduleVisualViewportUpdate);
    }
    if (viewportUpdateFrame) {
      window.cancelAnimationFrame(viewportUpdateFrame);
      viewportUpdateFrame = 0;
    }
    if (trackedLightbox) {
      trackedLightbox.style.removeProperty('--cart-preview-viewport-width');
      trackedLightbox.style.removeProperty('--cart-preview-viewport-height');
      trackedLightbox.style.removeProperty('--cart-preview-viewport-left');
      trackedLightbox.style.removeProperty('--cart-preview-viewport-top');
    }

    trackedVisualViewport = null;
    trackedLightbox = null;
  }

  /** Returns the interactive and changing elements inside the reusable lightbox. */
  function getLightboxElements(lightbox) {
    return {
      image: lightbox.querySelector('.cart-custom-preview-lightbox__image'),
      overlay: lightbox.querySelector('.cart-custom-preview-lightbox__overlay'),
      title: lightbox.querySelector('.cart-custom-preview-lightbox__title'),
      eyebrow: lightbox.querySelector('[data-cart-preview-eyebrow]'),
      switcher: lightbox.querySelector('[data-cart-preview-switcher]'),
      viewButtons: lightbox.querySelectorAll('[data-cart-preview-view]'),
      productViewButton: lightbox.querySelector(`[data-cart-preview-view="${PRODUCT_VIEW}"]`),
      uploadedViewButton: lightbox.querySelector(`[data-cart-preview-view="${UPLOADED_VIEW}"]`),
      statusTitle: lightbox.querySelector('[data-cart-preview-status-title]'),
      statusDescription: lightbox.querySelector('[data-cart-preview-status-description]'),
      fullSizeLink: lightbox.querySelector('[data-cart-preview-full-size]'),
      previousButton: lightbox.querySelector('[data-cart-preview-gallery-previous]'),
      nextButton: lightbox.querySelector('[data-cart-preview-gallery-next]'),
      galleryCounter: lightbox.querySelector('[data-cart-preview-gallery-counter]'),
      closeButton: lightbox.querySelector('[data-cart-preview-close]'),
      sourceThumbnails: lightbox.querySelector('[data-cart-preview-sources]'),
    };
  }

  /** Converts one thumbnail trigger into the image pair used by the dialog. */
  function getPreviewItem(trigger) {
    const productUrl = getSafeHttpUrl(trigger.dataset.previewUrl || trigger.getAttribute('href'));
    if (!productUrl) return null;

    const productTitle = trigger.dataset.productTitle || 'Your personalized design';
    const photoLabel = trigger.dataset.photoLabel || '';
    const designSources = getDesignSources(trigger, productUrl);
    const legacyOriginal = getSafeHttpUrl(trigger.dataset.originalUrl);
    const uploadedUrls = designSources.isCreative ? designSources.urls : legacyOriginal ? [legacyOriginal] : [];
    return {
      trigger,
      productUrl,
      uploadedUrl: uploadedUrls[0] || '',
      uploadedUrls,
      uploadedIndex: 0,
      isCreative: designSources.isCreative,
      sourceManifest: designSources.sourceManifest || null,
      // Completed product previews are flattened; no page-side overlay.
      overlayUrl: '',
      productAlt: trigger.dataset.previewAlt || 'Personalized product preview',
      uploadedAlt: photoLabel ? `Original uploaded photo: ${photoLabel}` : `Original uploaded photo for ${productTitle}`,
      productTitle,
      photoLabel,
      isGalleryItem: trigger.hasAttribute('data-cart-preview-gallery-item'),
      initialView: trigger.dataset.cartPreviewInitialView === UPLOADED_VIEW ? UPLOADED_VIEW : PRODUCT_VIEW,
    };
  }

  /**
   * Collects sibling thumbnails only when the clicked image belongs to a multi-photo group.
   * Ordinary single-image cart previews continue to behave exactly as before.
   */
  function getGalleryState(trigger) {
    const clickedItem = getPreviewItem(trigger);
    if (!clickedItem) return { items: [], index: 0 };

    const galleryRoot = trigger.closest('[data-cart-preview-gallery-root]');
    if (!galleryRoot) return { items: [clickedItem], index: 0 };

    const items = Array.from(
      galleryRoot.querySelectorAll('[data-cart-preview-gallery-item][data-cart-preview-zoom]')
    )
      .map((galleryTrigger) => getPreviewItem(galleryTrigger))
      .filter(Boolean);
    const clickedIndex = items.findIndex((item) => item.trigger === trigger);

    return {
      items: items.length ? items : [clickedItem],
      index: clickedIndex >= 0 ? clickedIndex : 0,
    };
  }

  /**
   * Switches between the finished product preview and the customer's uploaded photo.
   * Both images remain inside the same dialog so the cart and checkout context is preserved.
   */
  function setActiveView(lightbox, requestedView) {
    const elements = getLightboxElements(lightbox);
    const item = activeGalleryItems[activeGalleryIndex];
    if (!item) return;
    const currentOriginal = item.uploadedUrls[item.uploadedIndex] || '';
    lightbox.dataset.uploadedUrl = currentOriginal;
    const hasUploadedPhoto = Boolean(currentOriginal);
    const activeView = requestedView === UPLOADED_VIEW && hasUploadedPhoto ? UPLOADED_VIEW : PRODUCT_VIEW;
    const isUploadedView = activeView === UPLOADED_VIEW;
    const isGalleryItem = lightbox.dataset.galleryItem === 'true';
    const photoLabel = lightbox.dataset.photoLabel;
    const viewName = isUploadedView ? 'Original Photo' : isGalleryItem ? 'Photo Preview' : 'Your Preview';

    lightbox.dataset.activeView = activeView;
    elements.image.src = isUploadedView ? lightbox.dataset.uploadedUrl : lightbox.dataset.productUrl;
    elements.image.alt = isUploadedView
      ? lightbox.dataset.uploadedAlt + (item.uploadedUrls.length > 1 ? `, photo ${item.uploadedIndex + 1} of ${item.uploadedUrls.length}` : '')
      : lightbox.dataset.productAlt;
    const overlayUrl = isUploadedView ? '' : getSafePreviewOverlayUrl(lightbox.dataset.overlayUrl);
    if (overlayUrl) {
      elements.overlay.src = overlayUrl;
      elements.overlay.hidden = false;
    } else {
      elements.overlay.removeAttribute('src');
      elements.overlay.hidden = true;
    }
    elements.eyebrow.textContent = photoLabel ? `${photoLabel} · ${viewName}` : viewName;
    elements.statusTitle.textContent = isUploadedView
      ? 'Original photo received'
      : isGalleryItem
        ? 'Photo preview saved'
        : 'Personalized design saved';
    elements.statusDescription.textContent = isUploadedView
      ? item.isCreative && item.uploadedUrls.length > 1
        ? `Original photo ${item.uploadedIndex + 1} of ${item.uploadedUrls.length} used in this design.`
        : photoLabel
        ? `This is the original photo uploaded for ${photoLabel}.`
        : 'This is the original photo you uploaded for this item.'
      : isGalleryItem
        ? 'This is the saved preview for this photo.'
        : 'This is your personalized design preview.';

    elements.viewButtons.forEach((button) => {
      const isActive = button.dataset.cartPreviewView === activeView;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    const fullSizeUrl = isUploadedView ? lightbox.dataset.uploadedUrl : lightbox.dataset.productUrl;
    if (fullSizeUrl) {
      elements.fullSizeLink.href = fullSizeUrl;
      elements.fullSizeLink.textContent = isUploadedView
        ? 'Open uploaded photo (new tab) ↗' : 'Open your design preview (new tab) ↗';
      elements.fullSizeLink.hidden = false;
    } else {
      elements.fullSizeLink.removeAttribute('href');
      elements.fullSizeLink.hidden = true;
    }
    updatePhotoNavigation(lightbox, item, isUploadedView);
  }

  /** Updates the dialog URLs, labels, and navigation for one photo in the active group. */
  function showGalleryItem(lightbox, requestedIndex, requestedView) {
    if (!activeGalleryItems.length) return;

    activeGalleryIndex = Math.min(Math.max(requestedIndex, 0), activeGalleryItems.length - 1);
    const item = activeGalleryItems[activeGalleryIndex];
    const elements = getLightboxElements(lightbox);

    lightbox.dataset.productUrl = item.productUrl;
    lightbox.dataset.uploadedUrl = item.uploadedUrl;
    lightbox.dataset.overlayUrl = item.overlayUrl;
    lightbox.dataset.productAlt = item.productAlt;
    lightbox.dataset.uploadedAlt = item.uploadedAlt;
    lightbox.dataset.photoLabel = item.photoLabel;
    lightbox.dataset.galleryItem = String(item.isGalleryItem);
    elements.title.textContent = item.productTitle;
    elements.switcher.hidden = !item.uploadedUrl || item.uploadedUrl === item.productUrl;
    elements.productViewButton.textContent = item.isGalleryItem ? 'Photo preview' : 'Product preview';
    elements.uploadedViewButton.textContent = item.uploadedUrls.length > 1
      ? `Original photos (${item.uploadedUrls.length})`
      : item.isGalleryItem ? 'Original photo' : 'Uploaded photo';

    setActiveView(lightbox, requestedView);
    hydrateDesignOriginals(lightbox, item);
  }

  /** Preview arrows navigate product areas; creative originals remain inside their own design. */
  function updatePhotoNavigation(lightbox, item, isUploadedView) {
    const elements = getLightboxElements(lightbox);
    const viewingSources = isUploadedView && item.isCreative;
    const index = viewingSources ? item.uploadedIndex : activeGalleryIndex;
    const count = viewingSources ? item.uploadedUrls.length : activeGalleryItems.length;
    const noun = viewingSources ? 'Original photo' : 'Photo';
    elements.previousButton.hidden = count < 2;
    elements.nextButton.hidden = count < 2;
    elements.galleryCounter.hidden = count < 2;
    elements.previousButton.disabled = index === 0;
    elements.nextButton.disabled = index === count - 1;
    elements.previousButton.setAttribute('aria-label', `Previous ${noun.toLowerCase()}`);
    elements.nextButton.setAttribute('aria-label', `Next ${noun.toLowerCase()}`);
    elements.galleryCounter.textContent = `${viewingSources ? 'Original' : item.photoLabel || 'Photo'} ${index + 1} of ${count}`;
    elements.sourceThumbnails.hidden = !viewingSources || count < 2;
    if (!viewingSources || count < 2) {
      elements.sourceThumbnails.replaceChildren();
      return;
    }
    // Reuse buttons when only the active source changes, preserving keyboard focus.
    if (elements.sourceThumbnails.dataset.previewUrl !== item.productUrl || elements.sourceThumbnails.children.length !== count) {
      elements.sourceThumbnails.replaceChildren();
      elements.sourceThumbnails.dataset.previewUrl = item.productUrl;
      item.uploadedUrls.forEach((url, sourceIndex) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-custom-preview-lightbox__source';
        button.dataset.cartPreviewSource = String(sourceIndex);
        button.setAttribute('aria-label', `Original photo ${sourceIndex + 1} of ${count}`);
        const thumbnail = document.createElement('img');
        thumbnail.src = url;
        thumbnail.alt = '';
        thumbnail.width = 44;
        thumbnail.height = 44;
        thumbnail.loading = 'lazy';
        thumbnail.decoding = 'async';
        const number = document.createElement('span');
        number.textContent = String(sourceIndex + 1);
        button.append(thumbnail, number);
        elements.sourceThumbnails.appendChild(button);
      });
    }
    Array.from(elements.sourceThumbnails.children).forEach((button, sourceIndex) => {
      button.setAttribute('aria-pressed', String(sourceIndex === index));
    });
  }

  function showOriginal(lightbox, requestedIndex) {
    const item = activeGalleryItems[activeGalleryIndex];
    if (!item || !item.isCreative || requestedIndex < 0 || requestedIndex >= item.uploadedUrls.length) return;
    item.uploadedIndex = requestedIndex;
    setActiveView(lightbox, UPLOADED_VIEW);
  }

  /** Moves through all originals of one creative design, or through legacy photo areas. */
  function moveGallery(lightbox, direction) {
    const item = activeGalleryItems[activeGalleryIndex];
    if (item && item.isCreative && lightbox.dataset.activeView === UPLOADED_VIEW) {
      showOriginal(lightbox, item.uploadedIndex + direction);
      return;
    }
    const nextIndex = activeGalleryIndex + direction;
    if (nextIndex < 0 || nextIndex >= activeGalleryItems.length) return;
    showGalleryItem(lightbox, nextIndex, lightbox.dataset.activeView || PRODUCT_VIEW);
  }

  /** Opens the final product preview and enables in-dialog original-photo comparison when available. */
  function openPreview(trigger) {
    const galleryState = getGalleryState(trigger);
    if (!galleryState.items.length) return;

    const lightbox = getOrCreateLightbox();
    const elements = getLightboxElements(lightbox);

    if (lightbox.hidden) {
      previouslyFocusedElement = document.activeElement;
      previousBodyOverflow = document.body.style.overflow;
    }
    activeGalleryItems = galleryState.items;
    activeGalleryIndex = galleryState.index;

    showGalleryItem(lightbox, galleryState.index, activeGalleryItems[galleryState.index].initialView);
    lightbox.hidden = false;
    document.body.style.overflow = 'hidden';
    startVisualViewportTracking(lightbox);
    try {
      elements.closeButton.focus({ preventScroll: true });
    } catch (error) {
      elements.closeButton.focus();
    }
  }

  /** Closes the preview, clears image URLs, and restores keyboard focus to the image that opened it. */
  function closePreview() {
    const lightbox = document.getElementById(LIGHTBOX_ID);
    if (!lightbox || lightbox.hidden) return;

    const elements = getLightboxElements(lightbox);
    stopVisualViewportTracking();
    lightbox.hidden = true;
    document.body.style.overflow = previousBodyOverflow;

    elements.image.removeAttribute('src');
    elements.image.alt = '';
    elements.overlay.removeAttribute('src');
    elements.overlay.hidden = true;
    elements.fullSizeLink.removeAttribute('href');
    elements.fullSizeLink.hidden = true;
    elements.switcher.hidden = true;
    elements.previousButton.hidden = true;
    elements.nextButton.hidden = true;
    elements.galleryCounter.hidden = true;
    elements.galleryCounter.textContent = '';
    elements.sourceThumbnails.hidden = true;
    elements.sourceThumbnails.replaceChildren();
    delete elements.sourceThumbnails.dataset.previewUrl;
    delete lightbox.dataset.productUrl;
    delete lightbox.dataset.uploadedUrl;
    delete lightbox.dataset.overlayUrl;
    delete lightbox.dataset.productAlt;
    delete lightbox.dataset.uploadedAlt;
    delete lightbox.dataset.photoLabel;
    delete lightbox.dataset.galleryItem;
    delete lightbox.dataset.activeView;
    activeGalleryItems = [];
    activeGalleryIndex = 0;

    if (
      previouslyFocusedElement &&
      previouslyFocusedElement.isConnected &&
      typeof previouslyFocusedElement.focus === 'function'
    ) {
      try {
        previouslyFocusedElement.focus({ preventScroll: true });
      } catch (error) {
        previouslyFocusedElement.focus();
      }
    }
    previouslyFocusedElement = null;
  }

  hydrateCubeGalleries(document);
  if (document.body && window.MutationObserver) {
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach(hydrateCubeGalleries));
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(PREVIEW_TRIGGER_SELECTOR);
    if (trigger) {
      event.preventDefault();
      openPreview(trigger);
      return;
    }

    const viewButton = event.target.closest('[data-cart-preview-view]');
    if (viewButton) {
      event.preventDefault();
      const lightbox = viewButton.closest(`#${LIGHTBOX_ID}`);
      if (lightbox) setActiveView(lightbox, viewButton.dataset.cartPreviewView);
      return;
    }

    const sourceButton = event.target.closest('[data-cart-preview-source]');
    if (sourceButton) {
      event.preventDefault();
      const lightbox = sourceButton.closest(`#${LIGHTBOX_ID}`);
      if (lightbox) showOriginal(lightbox, Number(sourceButton.dataset.cartPreviewSource));
      return;
    }

    const previousButton = event.target.closest('[data-cart-preview-gallery-previous]');
    if (previousButton) {
      event.preventDefault();
      const lightbox = previousButton.closest(`#${LIGHTBOX_ID}`);
      if (lightbox) moveGallery(lightbox, -1);
      return;
    }

    const nextButton = event.target.closest('[data-cart-preview-gallery-next]');
    if (nextButton) {
      event.preventDefault();
      const lightbox = nextButton.closest(`#${LIGHTBOX_ID}`);
      if (lightbox) moveGallery(lightbox, 1);
      return;
    }

    const closeButton = event.target.closest('[data-cart-preview-close]');
    const lightbox = event.target.closest(`#${LIGHTBOX_ID}`);
    if (closeButton || (lightbox && event.target === lightbox)) {
      closePreview();
    }
  });

  document.addEventListener('keydown', (event) => {
    const lightbox = document.getElementById(LIGHTBOX_ID);
    if (event.key === 'Escape') {
      if (lightbox && !lightbox.hidden) {
        event.preventDefault();
        consumePreviewEscape = true;
        closePreview();
      }
      return;
    }

    consumePreviewEscape = false;
    const isTextInput =
      typeof event.target.matches === 'function' &&
      event.target.matches('input, textarea, select, [contenteditable="true"]');
    if (!lightbox || lightbox.hidden || isTextInput) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveGallery(lightbox, -1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveGallery(lightbox, 1);
    }
  });

  // Closing the preview restores focus inside the cart. Consume the matching
  // keyup so that the same Escape press does not close both dialogs.
  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape' || !consumePreviewEscape) return;
    consumePreviewEscape = false;
    event.preventDefault();
    event.stopPropagation();
  }, true);
  window.addEventListener('blur', () => { consumePreviewEscape = false; });
})();
